import React, { useMemo, useState } from "react";
import Table from "Components/DesignSystem/Table/Table";
import { TABBED_GRID_MAX_HEIGHT } from 'Components/DesignSystem/Table/pageGridHeight';
import Button from "Components/DesignSystem/Button/Button";
import Dialog from "Components/DesignSystem/Dialog/Dialog";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import Pagination from "Components/DesignSystem/Pagination/Pagination";
import Search from "Components/DesignSystem/Search/Search";
import Toggle from "Components/DesignSystem/Toggle/Toggle";
import Typography from "Components/DesignSystem/Typography/Typography";
import { DeleteIcon } from "assests/GmsIcons";
import { TableColumn } from "types/DesignSystem/Table";
import { SortType, DataRow, HeaderType } from "../RuleComponents/dataTypes";
import { RELATIONSHIPS } from "../../ISRConstants";

type TableProps = {
  id?: string,
  headers: HeaderType[],
  data: DataRow[],
  recordPerPage: number,
  setRecordPerPage: (val: number) => void,
  handlePageChange: (page: number) => void,
  handleSortByColumn: (map: string) => void,
  sort: SortType,
  loading: boolean,
  handleEdit: (index: number) => void,
  NoDataComponent: React.ComponentType,
  handleDelete: (index: number) => void,
  page: number,
  createMethod: () => void,
  createNode?: React.ReactNode,
  searchValue: string,
  onSearchChange: (value: string) => void,
  /**
   * How many rules the model has BEFORE the search filter — `data` is the filtered
   * list, so it cannot tell "no rules yet" from "no search matches". Zero (and not
   * loading) shows only the empty state. Omitted, `data.length` stands in.
   */
  totalCount?: number,
}

const PAGE_SIZE_OPTIONS = [
  { value: 10, label: "10" },
  { value: 20, label: "20" },
  { value: 50, label: "50" },
  { value: 100, label: "100" },
  { value: 10000, label: "All" },
];

/**
 * Per-column layout, keyed by the header's lowercased label — the successor to
 * the old `spaceObj` percentage map (25 / 15 / 50 / 15 / 5). Every column is
 * content-sized EXCEPT Description, which is deliberately widthless so it
 * absorbs the table's slack: it is the one column holding prose, and the legacy
 * map handed it a hardcoded 50% whether or not it had anything in it.
 */
const COLUMN_LAYOUT: Record<
  string,
  { width?: string; minWidth?: string; align?: "left" | "center" }
> = {
  "name": { width: "260px", minWidth: "200px" },
  "rule type": { width: "140px" },
  "description": { minWidth: "200px" },
  /* Left, like every other labelled column (QA, 2026-09-25). It was `center`, but
     the header's sort button fills the column, so "Enable/Disable" still read from
     the left edge while the switch sat centred under it — one column with two
     alignments. Header text and switch now start at the same x, as on stage. */
  "enable/disable": { width: "140px" },
  "delete": { width: "56px", align: "center" },
};

const titleCase = (value: string) => value.replace(/\b\w/g, (c) => c.toUpperCase());

const Table_ = ({
  id,
  headers,
  data,
  recordPerPage,
  setRecordPerPage,
  handlePageChange,
  handleSortByColumn,
  sort,
  loading,
  handleEdit,
  NoDataComponent,
  handleDelete,
  page,
  createMethod,
  createNode,
  searchValue,
  onSearchChange,
  totalCount,
}: TableProps) => {
  const baseId = id || "relationships";
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);

  /*
    `Rules.tsx` initialises `page` to 0 while the slice math below is 1-based, so
    page 0 would slice `(-recordPerPage, 0)` — an empty page. That never showed
    because the legacy `Paginator` normalised it from a mount effect
    (`redraw()` → `onPageChange(1)`); DS `Pagination` is controlled and does no
    such thing, so the floor lives here now. Removing it renders an empty list on
    first load with a clean tsc and green pure-function tests.
  */
  const currentPage = Math.max(1, page);
  const pageStart = (currentPage - 1) * recordPerPage;
  const pageRows = useMemo(
    () => data?.slice(pageStart, pageStart + recordPerPage) ?? [],
    [data, pageStart, recordPerPage]
  );

  const columns: TableColumn<DataRow>[] = useMemo(
    () =>
      headers.map(({ label, map, options }) => {
        const key = label?.toLowerCase();
        const layout = COLUMN_LAYOUT[key] ?? {};
        const base = {
          id: map,
          accessor: map as keyof DataRow,
          sortable: options?.sort === true,
          width: layout.width,
          minWidth: layout.minWidth,
          align: layout.align,
        };

        if (key === "delete") {
          return {
            ...base,
            // The column exists to hold an icon; a visible "DELETE" heading was
            // suppressed in the legacy markup too.
            header: "",
            sortable: false,
            render: (_value: unknown, row: DataRow, rowIndex: number) => (
              <Button
                variant="subtle"
                size="sm"
                aria-label={`Delete ${String(row?.name ?? "")}`}
                id={`delete-btn-${rowIndex}`}
                data-testid={`relationship-delete-${String(row?.id ?? rowIndex)}`}
                onClick={() => setPendingDeleteIndex(pageStart + rowIndex)}
              >
                {/* icon-follow-ink: DeleteIcon hardcodes #747593 strokes, so the
                    tone ink never reached it without the re-ink utility. */}
                <span className="tw-text-tone-danger-glyph icon-follow-ink tw-flex tw-items-center">
                  <DeleteIcon />
                </span>
              </Button>
            ),
          };
        }

        if (label === "Enable/Disable") {
          return {
            ...base,
            header: label,
            cellType: "toggle" as const,
            render: (_value: unknown, row: DataRow) => (
              <Toggle
                label=""
                aria-label={`Enable ${String(row?.name ?? "")}`}
                checked={!(row["is_disabled"] as boolean)}
                onChange={(_checked, event) => {
                  options?.handleSwitch && options.handleSwitch(event, row);
                }}
                data-testid={`relationship-toggle-${String(row?.id ?? "")}`}
              />
            ),
          };
        }

        return {
          ...base,
          header: label,
          render: (value: unknown, row: DataRow, rowIndex: number) => {
            const text =
              map === "rule_type" && typeof value === "string" ? titleCase(value) : value;
            if (!options?.isLink) {
              return <span className="tw-whitespace-break-spaces">{text as React.ReactNode}</span>;
            }
            return (
              <div
                role="button"
                tabIndex={0}
                id={`content-${key.replace(/[^a-z0-9]/g, "-")}-${rowIndex}`}
                data-testid={`relationship-name-${String(row?.id ?? rowIndex)}`}
                className="tw-cursor-pointer tw-whitespace-break-spaces tw-text-accent-bold hover:tw-underline"
                onClick={() => handleEdit(pageStart + rowIndex)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleEdit(pageStart + rowIndex);
                  }
                }}
              >
                {text as React.ReactNode}
              </div>
            );
          },
        };
      }),
    [headers, handleEdit, pageStart]
  );

  const pendingRow = pendingDeleteIndex !== null ? data[pendingDeleteIndex] : undefined;

  /*
    No rules at all → the empty state is the whole view (QA, 2026-09-25). A header
    row of sortable columns, a search box and a "Show 10" pager over an empty grid
    offered controls with nothing to act on, and the toolbar Create duplicated the
    empty state's own. A search with no matches is NOT this case: rules exist, so the
    grid and its controls stay. Never while loading, so it cannot flash on first load.
  */
  const hasNoRules = !loading && (totalCount ?? data?.length ?? 0) === 0;

  return (
    <>
      <div id="rules-outer-container">
        <div
          id="rules-header"
          className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-min-h-10 tw-px-2 tw-py-1"
        >
          <Typography
            variant="sub-title-label"
            as="h2"
            id="rules-title"
            className="tw-text-ink-secondary"
          >
            {RELATIONSHIPS}
          </Typography>
          {!hasNoRules && (
          <div className="tw-flex tw-items-center tw-gap-3">
            <Search
              placeholder="Search by names.."
              ariaLabel="Search relationships by name"
              value={searchValue}
              onChange={onSearchChange}
              size="sm"
              className="tw-w-56"
              data-testid={`${baseId}-search`}
            />
            {createNode ?? (
              <Button
                variant="tertiary"
                size="sm"
                id="create-rule-btn"
                data-testid={`${baseId}-create-btn`}
                onClick={createMethod}
              >
                Create
              </Button>
            )}
          </div>
          )}
        </div>

        {hasNoRules ? (
          <div data-testid={`${baseId}-empty`}>
            <NoDataComponent />
          </div>
        ) : (
        <Table<DataRow>
          /* Bound the grid so <main> stops scrolling the page — see
             `pageGridHeight.ts` for the measured reserve. `stickyHeader`
             needs it: sticky resolves against a scrolling ancestor. */
          stickyHeader
          maxHeight={TABBED_GRID_MAX_HEIGHT}
          columns={columns}
          data={pageRows}
          /* The string form carries DS Table's own `id ?? rowIndex` fallback —
             the same one the per-row testids below use — so a row that somehow
             arrives without an id still gets a unique key. */
          rowKey="id"
          striped
          loading={loading}
          sortState={{
            columnId: sort.column ?? "",
            direction: sort.direction === "asc" ? "asc" : sort.direction === "desc" ? "desc" : null,
          }}
          onSort={(columnId) => handleSortByColumn(columnId)}
          emptyContent={<NoDataComponent />}
          data-testid={`${baseId}-table`}
        />
        )}
      </div>

      {!hasNoRules && (
      <div id="pagination-section" className="tw-py-2">
        <div
          id="pagination-container"
          className="tw-flex tw-justify-end tw-items-center tw-gap-3 tw-flex-wrap"
        >
          <div id="pagination-page-size" className="tw-flex tw-items-center tw-gap-2">
            <span>Show</span>
            <Dropdown
              label=""
              aria-label="Records per page"
              size="sm"
              className="tw-w-[84px]"
              options={PAGE_SIZE_OPTIONS}
              value={recordPerPage}
              onChange={(value) => setRecordPerPage(Number(value))}
              data-testid={`${baseId}-page-size`}
            />
          </div>
          <Pagination
            id="rules-paginator"
            page={currentPage}
            totalItems={data?.length ?? 0}
            pageSize={recordPerPage}
            onPageChange={handlePageChange}
            size="sm"
            data-testid={`${baseId}-pagination`}
          />
        </div>
      </div>
      )}

      <Dialog
        isOpen={pendingDeleteIndex !== null}
        onClose={() => setPendingDeleteIndex(null)}
        title="Delete Relationship"
        tone="danger"
        size="sm"
        data-testid="relationship-delete-dialog"
        footer={
          <div className="tw-flex tw-w-full tw-justify-end tw-items-center tw-gap-3">
            <Button
              variant="secondary"
              size="sm"
              id="delete-relationship-cancel-btn"
              data-testid="relationship-delete-cancel-btn"
              onClick={() => setPendingDeleteIndex(null)}
            >
              Cancel
            </Button>
            {/* `tone="danger"` + a PRIMARY confirm is the settled destructive-
                confirm pattern (§ 84/§ 92, and spelled out in Button.tsx): the
                `danger` variant is for a destructive action sitting in a row or
                toolbar, not for the confirm inside a toned dialog. This is what
                the model, AB-test and Input-Parameter deletes already render. */}
            <Button
              variant="primary"
              size="sm"
              id="delete-relationship-confirm-btn"
              data-testid="relationship-delete-confirm-btn"
              onClick={() => {
                if (pendingDeleteIndex !== null) handleDelete(pendingDeleteIndex);
                setPendingDeleteIndex(null);
              }}
            >
              Delete
            </Button>
          </div>
        }
      >
        <Typography variant="body-primary" as="p" id="delete-relationship-message">
          Are you sure you want to delete the relationship named{" "}
          <strong>&quot;{pendingRow?.["name"] as string}&quot;</strong> of type{" "}
          <strong>
            &quot;{titleCase((pendingRow?.["rule_type"] as string) ?? "")}&quot;
          </strong>
          ?
          <br />
          This action cannot be undone.
        </Typography>
      </Dialog>
    </>
  );
};

export default Table_;
